import api from './client';
import { Listing } from './listings';

export interface Agent {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'agent' | 'user';
  isApproved: boolean;
  /** Profile photo URL, or null. */
  avatar?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AgentListParams {
  search?: string;
  isApproved?: boolean;
  page?: number;
  limit?: number;
}

export interface AgentListResponse {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  agents: Agent[];
}

interface RawListResponse extends AgentListResponse {
  status: number;
}

interface MutationResponse {
  status: number;
  message?: string;
  error?: string | string[];
  agent?: Agent;
}

/** GET /api/agents */
export const listAgents = async (
  params: AgentListParams = {}
): Promise<AgentListResponse> => {
  const query: Record<string, string> = {};
  if (params.search) query.search = params.search;
  if (params.isApproved === true) query.isApproved = 'true';
  if (params.isApproved === false) query.isApproved = 'false';
  if (params.page) query.page = String(params.page);
  if (params.limit) query.limit = String(params.limit);

  const res = await api.get<RawListResponse>('/agents', { params: query });
  return {
    total: res.data.total,
    page: res.data.page,
    limit: res.data.limit,
    totalPages: res.data.totalPages,
    agents: res.data.agents,
  };
};

/** GET /api/agents/:id */
export const getAgent = async (id: number): Promise<Agent> => {
  const res = await api.get<{ status: number; agent: Agent }>(`/agents/${id}`);
  return res.data.agent;
};

export interface AgentStats {
  totalListings: number;
  activeListings: number;
  inactiveListings: number;
  totalViews: number;
  inquiries: number;
  applications: number;
}

export interface AgentSubscription {
  id: number;
  planName: string | null;
  billingCycle: 'monthly' | 'yearly' | null;
  /** Sequelize DECIMAL ships as a string over JSON. */
  amount: string | number | null;
  currency: string | null;
  status: string;
  startDate: string | null;
  expiryDate: string | null;
  cancelledAt: string | null;
  autoRenew: boolean;
}

export interface AgentDetail {
  agent: Agent & { provider?: string | null };
  stats: AgentStats;
  /** Plan the listing limit is enforced from; listingLimit null = unlimited. */
  plan: { name: string; listingLimit: number | null };
  /** Most recent subscription of any status, or null if they never had one. */
  subscription: AgentSubscription | null;
  listings: Listing[];
}

/** GET /api/agents/:id — the agent plus stats, plan, subscription and listings. */
export const getAgentDetail = async (id: number): Promise<AgentDetail> => {
  const res = await api.get<{ status: number } & AgentDetail>(`/agents/${id}`);
  const { agent, stats, plan, subscription, listings } = res.data;
  return { agent, stats, plan, subscription, listings };
};

/** PATCH /api/agents/:id/approval — omit body to toggle. */
export const setAgentApproval = async (
  id: number,
  isApproved?: boolean
): Promise<Agent> => {
  const body = typeof isApproved === 'boolean' ? { isApproved } : {};
  const res = await api.patch<MutationResponse>(`/agents/${id}/approval`, body);
  if (!res.data.agent) throw new Error('No merchant returned');
  return res.data.agent;
};

/** DELETE /api/agents/:id */
export const deleteAgent = async (id: number): Promise<void> => {
  await api.delete<MutationResponse>(`/agents/${id}`);
};

/** Deterministic HSL hue from a numeric id (matches the design's per-agent colors). */
export const hueFromId = (id: number): number => (id * 67) % 360;

/** Pull initials from a name string. */
export const initialsFromName = (name: string): string =>
  name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

/** Format an ISO date into "Mar 3, 2026" style. */
export const formatJoinedDate = (iso: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};
